import type { UserProfile } from '../types/user';

/**
 * A stored displayName that is really the old placeholder rather than a name.
 * createUserProfile stamped it on every profile that arrived without one, so
 * accounts created before that stopped still carry it.
 */
const LEGACY_PLACEHOLDER = 'User';

/**
 * Last resort. Unreachable in practice: `email` is required on UserProfile and
 * Firebase Auth will not mint a user without one, so the email branch always
 * has something to return.
 */
const UNNAMED = 'Player';

/**
 * The name to denormalise onto a chat document.
 *
 * Conversation and message documents keep a copy of a participant's name taken
 * at write time, and nothing goes back to correct it afterwards. So the copy
 * must never be a placeholder: a player has no displayName until onboarding
 * step 1 collects one, and anything sent before then would carry that
 * placeholder for the life of the document.
 *
 * The chain is the name they chose, then whichever first name the profile
 * already holds, then the local part of their email. 'User' is not in it, and a
 * profile still storing that literal is treated as unnamed, so an old account
 * heals on its next write instead of propagating it further.
 *
 * On a parent account the first name on file is the child's. That is already
 * what coach-facing surfaces show as the primary label (playerDisplayName in
 * parentAccountDisplay.ts), so this follows the existing convention rather than
 * introducing a second one.
 */
export function chatParticipantName(profile: UserProfile | null | undefined): string {
  if (!profile) return UNNAMED;
  const candidates = [
    profile.displayName,
    profile.playerFirstName,
    profile.parentFirstName,
    profile.email?.split('@')[0],
  ];
  for (const candidate of candidates) {
    const name = (candidate ?? '').trim();
    if (name && name !== LEGACY_PLACEHOLDER) return name;
  }
  return UNNAMED;
}
